import { ApplicationRef, Injectable, NgZone, inject } from '@angular/core';
import { datadogRum } from '@datadog/browser-rum';

/** The Angular service worker script; the only worker this app registers. */
export const SW_SCRIPT = 'ngsw-worker.js';

/** Same as Angular's default strategy: register once the app is stable, or after 30s if it never settles. */
const REGISTER_TIMEOUT_MS = 30000;

/**
 * S2N-3226: WebKit rejects service worker jobs (register, update) from a WKWebView whose domain the
 * host app has not declared in WKAppBoundDomains, which is what the iOS App Store wrapper hits. That
 * is a limit of the environment rather than an application failure, so it is recognised here and
 * reported once instead of surfacing as an error on every page load.
 */
export function isAppBoundDomainRejection(err: unknown): boolean {
    return err instanceof TypeError && /non app-bound domain/i.test(err.message);
}

/**
 * The app's single service worker registration (S2N-3226). Before this, index.html, main.ts,
 * ServiceWorkerModule and every router navigation each registered ngsw-worker.js, mostly without
 * handling a rejection. ServiceWorkerModule is now told never to register (see app.module.ts) so
 * SwUpdate keeps working while this owns the call; Angular's own failure log is an opaque NG05604 in
 * production. Navigation never registers or updates the worker: the worker checks for a new version
 * on each page load, and CheckForUpdateService polls during long sessions.
 */
@Injectable({ providedIn: 'root' })
export class ServiceWorkerRegistrationService {

    private appRef = inject(ApplicationRef);
    private zone = inject(NgZone);
    private started = false;

    /** True once the environment has refused service workers; update checks are skipped. */
    unsupported = false;

    register(): void {
        if (this.started || !('serviceWorker' in navigator)) { return; }
        this.started = true;

        this.zone.runOutsideAngular(() => {
            const timeout = new Promise<void>(resolve => setTimeout(resolve, REGISTER_TIMEOUT_MS));
            Promise.race([this.appRef.whenStable(), timeout])
                .then(() => {
                    if (this.appRef.destroyed) { return; }
                    return navigator.serviceWorker.register(SW_SCRIPT, { updateViaCache: 'none' })
                        .then(reg => console.log('Service worker registered', reg.scope));
                })
                .catch(err => this.handleFailure(err));
        });
    }

    /** Records an app-bound-domain refusal as an expected environment limit; anything else is a real failure. */
    handleFailure(err: unknown): void {
        if (isAppBoundDomainRejection(err)) {
            this.unsupported = true;
            datadogRum.addAction('service-worker-unavailable', { reason: 'non-app-bound-domain' });
            console.warn('Service workers are unavailable in this app shell (non app-bound domain).', err);
            return;
        }
        console.error('Service worker registration failed:', err);
    }
}
